/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges,
} from '@angular/core';
import { SvgIconRegistry } from '@ngneat/svg-icon';
import { bulbXTwitterIcon } from 'IconLibrary/bulb/x-twitter';
import { faFacebookFIcon } from 'IconLibrary/fontawesome/brands/facebook-f';
import { faEnvelopeIcon } from 'IconLibrary/fontawesome/regular/envelope';
import { bulbGoogleClassroomIcon } from 'IconLibrary/bulb/google-classroom';
import { faLinkedinInIcon } from 'IconLibrary/fontawesome/brands/linkedin-in';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

interface ShareService {
  title: string,
  href: string,
  svgIconKey: string,
  isDisabled: boolean,
  disabledReason: string,
}

@Component({
  selector: 'bulb-social-share',
  templateUrl: './social-share.component.html',
  styleUrls: ['./social-share.component.scss'],
  standalone: false,
})
export class SocialShareComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Variable to house the observable for translating the email subject
   */
  private translateEmailSubject: Subscription;

  /**
 * The current url of the page to be used when a social icon is clicked. This value gets
 * injected into the share url to be used in whichever social medium is clicked
 */
  private currentPublicUrl: string = window.location.href;

  /**
   * Reference to the index of shareServices map that contains email service information. Initialized onInit
   */
  private emailService: any;

  /**
   * Reference to the index of shareServices map that contains googleClassroom service information. Initialized onInit
   */
  private googleClassroomService: any;

  /**
   * Reference to the index of shareServices map that contains facebook service information. Initialized onInit
   */
  private facebookService: any;

  /**
   * Reference to the index of shareServices map that contains twitter service information. Initialized onInit
   */
  private twitterService: any;

  /**
   * Reference to the index of shareServices map that contains linkedIn service information. Initialized onInit
   */
  private linkedInService: any;

  /*
  * Input that dictates which URL to show when a social icon is clicked
  */
  @Input() shareUrl: string;

  /**
   * Variable to dictate visibility
   */
  @Input() disabledShareLink: boolean;

  /**
   * Variable to dictate visibility dependant on org permissions around social sharing
   */
  @Input() socialPlatformsDisabledByOrg: boolean;

  /**
   * Social platforms visibility state for accounts that have social share enabled.
   * Used to determine which tooltip to show.
   */
  @Input() socialPlatformsDisabledByShareState: boolean;

  /**
   * True if user has free account.
   */
  @Input() isFreeUser: boolean;

  /**
   * Map of share service information used in template
   */
  public shareServices: Map<string, ShareService> = new Map([
    [
      'email',
      {
        title: 'socialShare.email',
        href: '',
        svgIconKey: 'envelope',
        isDisabled: false,
        disabledReason: '',
      },
    ],
    [
      'googleClassroom',
      {
        title: 'socialShare.googleClassroom',
        href: '',
        svgIconKey: 'google-classroom',
        isDisabled: false,
        disabledReason: '',
      },
    ],
    [
      'facebook',
      {
        title: 'socialShare.facebook',
        href: '',
        svgIconKey: 'facebook-f',
        isDisabled: false,
        disabledReason: '',
      },
    ],
    [
      'twitter',
      {
        title: 'socialShare.twitter',
        href: '',
        svgIconKey: 'x-twitter',
        isDisabled: false,
        disabledReason: '',
      },
    ],
    [
      'linkedIn',
      {
        title: 'socialShare.linkedIn',
        href: '',
        svgIconKey: 'linkedin-in',
        isDisabled: false,
        disabledReason: '',
      },
    ],
  ]);

  constructor(private svgRegistry: SvgIconRegistry, private transloco: TranslocoService) {
    this.svgRegistry.register([
      bulbXTwitterIcon,
      faEnvelopeIcon,
      faFacebookFIcon,
      bulbGoogleClassroomIcon,
      faLinkedinInIcon,
    ]);
  }

  ngOnInit() {
    this.emailService = this.shareServices.get('email');
    this.googleClassroomService = this.shareServices.get('googleClassroom');
    this.facebookService = this.shareServices.get('facebook');
    this.twitterService = this.shareServices.get('twitter');
    this.linkedInService = this.shareServices.get('linkedIn');

    this.updateDisabledStates();

    this.translateEmailSubject = this.transloco
      .selectTranslateObject('socialShare.emailLink', { 'body.owner': { url: this.shareUrl } })
      .subscribe((result) => {
        this.emailService.href = `mailto:?subject=${result.subject?.owner}&body=${result.body?.owner}`;
      });
    this.googleClassroomService.href = `https://classroom.google.com/share?url=${this.shareUrl}`;
    this.facebookService.href = `https://www.facebook.com/sharer/sharer.php?u=${this.currentPublicUrl}`;
    this.twitterService.href = `https://x.com/intent/post?url=${this.currentPublicUrl}`;
    this.linkedInService.href = `http://www.linkedin.com/shareArticle?mini=true&url=${this.currentPublicUrl}`;
  }

  /**
   * Updates whether a specific share service is disabled and if so the reason why for conditionally injecting
   * translations in template
   */
  updateDisabledStates() {
    this.shareServices.forEach((v, k) => {
      if (k === 'email' && (this.disabledShareLink)) {
        v.isDisabled = true;
        v.disabledReason = 'sharePanel.disabled.admin';
      } else if (k === 'googleClassroom' && (this.disabledShareLink || this.isFreeUser)) {
        v.isDisabled = true;
        v.disabledReason = this.isFreeUser ? 'sharePanel.disabled.onlyBulbPlus' : 'sharePanel.disabled.admin';
      } else if (k !== 'email' && k !== 'googleClassroom') {
        if (this.socialPlatformsDisabledByOrg || this.socialPlatformsDisabledByShareState) {
          v.isDisabled = true;
          v.disabledReason = this.socialPlatformsDisabledByOrg
            ? 'sharePanel.disabled.admin' : 'sharePanel.disabled.publicAvailability';
        } else {
          v.isDisabled = false;
          v.disabledReason = v.title;
        }
      } else {
        v.isDisabled = false;
        v.disabledReason = v.title;
      }
    });
  }

  /**
   * This is needed to force the shareServices map to display in order we want using the keyvalue pipe in the template
   * @returns -1
   */
  // eslint-disable-next-line class-methods-use-this
  shareServicesOrder() {
    return 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.socialPlatformsDisabledByOrg || changes.socialPlatformsDisabledByShareState) {
      this.updateDisabledStates();
    }
  }

  ngOnDestroy() {
    this.translateEmailSubject.unsubscribe();
  }
}
